import { useSuspenseQuery } from "@apollo/client/react";
import { useCallback, useMemo } from "react";
import {
  GetEvidencesDocument,
  type GetEvidencesQuery,
  type GetEvidencesQueryVariables,
} from "~/features/evidences/gqls";
import type { EvidenceListFilters, SortDirection } from "~/features/evidences/types";
import { buildEvidencesVariables } from "~/features/evidences/utils/transformers";
import { useTenant } from "~/features/tenant/context";
import { useEvidenceCount } from "./useEvidenceCount";

interface UseEvidenceListArgs {
  filters: EvidenceListFilters;
  direction: SortDirection;
  pageIndex: number;
  pageSize: number;
}

/**
 * Server-paginated list of leaf evidences for the current tenant. Variables
 * (where / orderBy / limit / offset) are built from the URL-derived filters.
 */
export function useEvidenceList({ filters, direction, pageIndex, pageSize }: UseEvidenceListArgs) {
  const { tenantId } = useTenant();
  if (!tenantId) throw new Error("Tenant ID is required");
  const variables = useMemo(
    () => buildEvidencesVariables({ tenantId, filters, direction, pageIndex, pageSize }),
    [direction, filters, pageIndex, pageSize, tenantId],
  );
  const { data, refetch: refetchList } = useSuspenseQuery<GetEvidencesQuery, GetEvidencesQueryVariables>(
    GetEvidencesDocument,
    { variables },
  );
  const { count, refresh: refreshCount } = useEvidenceCount({ filters });
  const rows = data.evidences.slice(0, pageSize);

  const refetch = useCallback(async () => {
    await Promise.all([refetchList(), refreshCount()]);
  }, [refetchList, refreshCount]);

  return {
    rows,
    rowsLength: data.evidences.length,
    totalCount: count,
    refetch,
  };
}
